<script>
	/* ─────────────── imports ─────────────── */
	import ProductCardNew from '$components/ProductCardNew.svelte';
	import ProductCardEnded from '$components/ProductCardEnded.svelte';
	import Loading from '$lib/Loading.svelte';
	import ReviewsComponent from '$components/ReviewsComponent.svelte';
	import DrawCountdown from '$components/DrawCountdown.svelte';
	import InstantWinList from '$components/InstantWinList.svelte';
	import CheckNumbers from '$components/CheckNumbers.svelte';
	import NotificationTrigger from '$components/NotificationTrigger.svelte';
	import { FacebookPixel, fb } from '@beyonk/svelte-facebook-pixel';
	import posthog from 'posthog-js';
	import { endingProductsStore } from '$stores/products.js';
	import { prizes, subscribeToPrizes } from '$stores/prizes.js';
	import { isAppUser } from '$stores/isAppUser.js';
	import { page } from '$app/stores';
	import { browser } from '$app/environment';
	import { onMount } from 'svelte';
	import SeoComp from '$lib/SeoComp.svelte';



	/* ─────────────── data from server ─────────────── */
	export let data; // provided by +page.js
	const { product, instantWins = [], seo } = data;

	/* ─────────────── local state ─────────────── */
	let thisData = product;
	let loading = !product?.name;
	let showDescription = true;
	let pid;
	let endFormattedDate;
	let startFormattedDate;
	let maxStock = 1000;
	let dynamicStock = 0;

	/* ─────────────── lifecycle ─────────────── */
	onMount(() => {
		if (!browser) return;

		const unsubscribe = subscribeToPrizes();

		pid = $page.params.pid || product?.id;
		if (thisData && !thisData.id) thisData.id = pid;

		if (thisData?.stock && thisData.stock < maxStock) {
			maxStock = thisData.stock;
		}
		if (thisData?.name) loading = false;

		endFormattedDate = new Date(thisData?.endDate).toLocaleDateString('en-GB');
		startFormattedDate = new Date(thisData?.createdAt).toLocaleDateString('en-GB');

		/* ── analytics ───────────────────────── */
		posthog.capture('competition_viewed', {
			currency: 'GBP',
			value: thisData?.price,
			item_id: thisData?.name?.replace(' ', '_'),
			item_name: thisData?.name,
			price: thisData?.price
		});

		fb.track('ViewContent', {
			content_ids: thisData?.id,
			content_category: thisData?.category,
			content_name: thisData?.name,
			content_type: 'prize',
			contents: thisData,
			currency: 'GBP',
			value: thisData?.price
		});

		window.klaviyo.track('Viewed Product', {
			ProductName: thisData?.name,
			ProductID: thisData?.id,
			SKU: thisData?.id,
			Categories: [thisData?.category],
			ImageURL: thisData?.featured_image,
			URL: `https://www.hotdraw.co.uk/prizes/${thisData?.id}`,
			Price: thisData?.price
		});

		window.klaviyo.trackViewedItem({
			Title: thisData?.name,
			ItemId: thisData?.id,
			Categories: thisData?.category,
			ImageUrl: thisData?.featured_image,
			Url: `https://www.hotdraw.co.uk/prizes/${thisData?.id}`,
			Metadata: { Brand: thisData?.name, Price: thisData?.price }
		});

		window.klaviyo.track('Viewed Category', {
			CategoryName: thisData?.category,
			CategoryID: thisData?.category,
			ImageURL: thisData?.featured_image
		});

		return () => unsubscribe();
	});

	/* ─────────────── reactive: stock sync ─────────────── */
	$: {
		if (pid) {
			const prize = $prizes.find((p) => p.id === pid);
			dynamicStock = prize ? prize.stock : 0;
		} else {
			dynamicStock = 0;
		}
	}
</script>

<!-- ░░░░░░ SEO tags ░░░░░░ -->
{#if seo}
<SeoComp {...seo} />
{/if}

<div class:is-active={loading} class="container">
	<div class="breadcrumb">
		<p><a href="/">Home</a> - <a href="/prizes">Prizes</a></p>
	</div>

	{#if thisData}
		<div class="countdown-product">
			{#if thisData.stock === 0}
				<h3>Competition Closed</h3>
			{/if}
		</div>
	{/if}

	<article class="product">
		{#await thisData}
			<svelte:component this={Loading} />
		{:then thisData}
			{#if thisData}
				<h1>{thisData.name}</h1>

				<div class="product-wrap">
					{#if thisData.stock > 0}
						<ProductCardNew
							isUpsell={false}
							isHighlight={thisData.highlight}
							isEnded={false}
							isListing={false}
							data={thisData}
							isSingle={true}
						/>

						<div class="prize-info-block">
							{#if instantWins.length}
								<InstantWinList {instantWins} />
							{/if}

							<article>
								{#if $isAppUser}
									<p class="small-p">Apple is not a sponsor of this competition.</p>
									<p class="small-p">
										Rules for our competitions can be found in our
										<a href="https://www.hotdraw.co.uk/terms">Terms and Conditions</a>.
									</p>
								{/if}

								{#if thisData.description}
									<button class="btn-four" on:click={() => (showDescription = !showDescription)}>
										{showDescription ? 'Hide' : 'Show'} description
									</button>

									{#if showDescription}
										<div class="page-description w-full">
											{#if thisData.category !== 'subscriber'}
												<p>Tickets: {thisData.initStock}</p>
												<p>Remaining: {dynamicStock}</p>
											{/if}
											<p>{@html thisData.description}</p>
										</div>
									{/if}
								{/if}
							</article>

							<div class="info">
								<div class="center">
									<p>Start date: {startFormattedDate}</p>
									<p>End date: {endFormattedDate}</p>
								</div>
								<p>
									Free entry route available, <br />
									please <a href="/terms#free">click here</a> for details
								</p>
							</div>
						</div>
					{:else}
						<!-- competition closed -->
						<ProductCardEnded {pid} data={thisData} isSingle={true} isListing={false} />

						<div class="prize-info-block">
							{#if instantWins.length}
								<InstantWinList {instantWins} />
							{/if}
						</div>

						{#if !thisData.winnerChosen}
							<DrawCountdown />
						{/if}

						<article>
							{#if thisData.description}
								<button class="btn-four" on:click={() => (showDescription = !showDescription)}>
									{showDescription ? 'Hide' : 'Show'} description
								</button>

								{#if showDescription}
									<div class="page-description">
										<p>{@html thisData.description}</p>
									</div>
								{/if}
							{/if}
						</article>
					{/if}
				</div>
			{/if}

			{#if $endingProductsStore.length}
				<div>
					<h4>Last chance</h4>
					<ul class="no-format flex list-flex">
						{#each $endingProductsStore as product}
							{#if product.id !== pid}
								<li class="productCard-wrap">
									<ProductCardNew
										isUpsell={false}
										isHighlight={false}
										isListing={true}
										isSingle={false}
										isEnded={false}
										data={product}
									/>
								</li>
							{/if}
						{/each}
					</ul>
				</div>
			{/if}
		{/await}
	</article>

	{#if thisData.category === 'subscriber'}
		<CheckNumbers assignedNumbers={thisData.assignedNumbers} />
	{/if}

	<ReviewsComponent showText={true} />
</div>

<style lang="scss">
	.btn-four {
		margin: 25px 0;
		font-weight: 700;
		letter-spacing: 0.01em;
		font-size: 18px;
	}
	.productCard-wrap {
		min-width: 280px;
	}

	@media (min-width: 749px) {
		article.product .product-wrap {
			display: flex;
			flex-wrap: wrap;
			> div {
				flex: 1;
			}
		}
	}

	.container {
		min-height: 70vh;
	}
	.is-active {
		visibility: hidden;
		opacity: 0;
		transition: ease 0.2s;
	}

	.countdown-product {
		text-align: center;
	}

	.info {
		background: #f1f1f1;
		padding: 0.25rem 1.5rem;
		p {
			margin: 5px 0;
		}
		> p {
			text-align: center;
			font-size: 0.9em;
		}
	}

	.page-description {
		max-height: 420px;
		overflow-y: auto;
		padding: 6px 12px;
		border-radius: 4px;
		margin-bottom: 20px;
		&::-webkit-scrollbar {
			width: 12px;
		}
		&::-webkit-scrollbar-track {
			background: lightgray;
			border-radius: 20px;
		}
		&::-webkit-scrollbar-thumb {
			background: #fe1100;
			border-radius: 20px;
			border: 2px solid #fff;
		}
	}

	.prize-info-block {
		width: 100%;
	}
	@media (min-width: 789px) {
		.prize-info-block {
			width: calc(100% - 460px);
		}
	}
</style>
